import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/routes";
import { supabase } from "@/lib/supabase";
import { Profile } from "@shared/schema";
import { useLocation } from "wouter";
import { useEffect, useCallback } from "react";
import { authFetch } from "@/lib/authFetch";

export function useAuth() {
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session ?? null;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: [api.auth.me.path, session?.user?.id],
    queryFn: async () => {
      const res = await authFetch(api.auth.me.path);
      if (!res.ok) {
        if (res.status === 401) return null;
        throw new Error("Failed to fetch profile");
      }
      return (await res.json()) as Profile;
    },
    enabled: !!session?.access_token,
    retry: 1,
    staleTime: 30000,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      queryClient.setQueryData(['auth', 'session'], newSession ?? null);
      if (newSession) {
        queryClient.invalidateQueries({ queryKey: [api.auth.me.path] });
      } else {
        queryClient.removeQueries({ queryKey: [api.auth.me.path] });
      }
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);

  const isLoading = isSessionLoading || (!!session && isProfileLoading);
  const isAuthenticated = !!session && !!profile;

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    setLocation('/login');
  }, [queryClient, setLocation]);

  return {
    session,
    profile,
    isLoading,
    isAuthenticated,
    logout,
    user: session?.user,
  };
}
